import { CommonModule } from '@angular/common';
import { Component, WritableSignal, computed, effect, signal } from '@angular/core';
import { FormsModule, Validators } from "@angular/forms";
import { EzBaseComponent } from "@template-ui/components/ez-base.component";
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { InputConfig } from "@template-ui/types";
import { ToggleSwitch } from "primeng/toggleswitch";

// Move type outside the class!
type Profile = {
  firstName: string;
  lastName: string;
  email: string;
  age: number | null;
  password: string;
  confirmPassword: string;
};

@DocMeta({
  hide: true,
  showcaseTitle: "Using a signal for formValues",
    showcaseDesc: `
    This sample demonstrates using a signal for the formValues input on the EZ-Form. Form A and Form B use the same config object and share the formValues object or signal.
    Just like when using an object for the formValues (not a signal), the values stay synchronized between the two forms and with the third block that is not using a form.

    The advantage of using a signal for the formValues is that computed()s can be created from it which can be used for disabling, hiding, setting other properties etc. Effects
    can also be written to do something automatically when a formValue changes.

    In this sample, if the First Name is set to 'Kyle' (entered in any of the 3 First Name inputs) while using a signal, then the input is disabled via a computed() and the From effect() field will also update.

    Conclusion: Always use a signal for formValues? Because you might want to create a computed() and so far, there's no downside.... I think.

    NOTE: If you use a signal for formValues, you MUST use bannana box notation, i.e. [(formValues)]="MyFormValuesSig". I'm not sure why yet or if we can fix it but be aware for now.
  `
})
@Component({
    selector: 'ui-form-signal-sample, ez-form-signal-sample',
    templateUrl: './ez-form-signal-sample.component.html',
    styleUrl: './ez-form-signal-sample.component.scss',
    imports: [
        CommonModule, FormsModule,
        UiLibraryModule,
        ToggleSwitch
    ]
})
export class EzFormSignalSampleComponent extends EzBaseComponent
{

  /**
   * Tied to the toggler to test using a signal or plain object
   */
  useSignal = signal<boolean>(false);

  // Demonstrate using an effect
  lastUpdateMsg?: string;

  /* formValues as plain object */
  formValues = {
    firstName: "",
    lastName: "",
    email: "",
    age: null,
    password: "",
    confirmPassword: ""
  };


  /* formValues as signal */
  formValuesSig: WritableSignal<Profile> = signal<Profile>({
    firstName: "",
    lastName: "",
    email: "",
    age: null,
    password: "",
    confirmPassword: ""
  });

  // computed()s (will only work of formValues is a signal)
  computedFirstName = computed(() => this.useSignal() ? this.formValuesSig().firstName.toUpperCase() : this.formValues.firstName.toUpperCase());
  disableInput = computed(() => this.useSignal() ? this.formValuesSig().firstName == 'Kyle' : this.formValues.firstName == 'Kyle');

  validationsFormConfig: InputConfig[] = [
    { name: 'firstName', type: 'text', label: 'First Name', row: 1, width: '2x', validation:  [Validators.required ], validationMessages: { required: 'First Name is required' } },
    { name: 'lastName', type: 'text', label: 'Last Name', row: 2, width: '2x', validation:  [Validators.required], validationMessages: { required: 'Last Name is required' } },
    { name: 'email', type: 'text', label: 'Email', row: 3, width: '1x', validation:  [Validators.required, Validators.email], validationMessages: { required: 'Email is required', email: 'Email must be a valid email address' } },
    { name: 'age', type: 'number', label: 'Age', row: 3, width: '1x', validation:  [Validators.min(18), Validators.max(99)], validationMessages: { min: 'Age must be at least 18', max: 'Age must be less than 99' } },
    // { name: 'password', type: 'password', label: 'Password', row: 4, width: '2x', validation:  [Validators.required, Validators.minLength(6)], validationMessages: { required: 'Password is required', minlength: 'Password must be at least 6 characters' } },
    // { name: 'confirmPassword', type: 'password', label: 'Confirm Password', row: 5, width: '2x', validation:  [Validators.required], validationMessages: { required: 'Confirm Password is required' } }
  ];

  constructor()
  {
    super();
    this.thisClassName = 'EzFormSignalSampleComponent';
    effect(() => {
      // Do stuff that must wait for a signal
      this.lastUpdateMsg = this.formValuesSig().firstName || this.formValues.firstName;  //<-- won't actually use this.formValues.firstName because effect won't fire.
    })
  }

  // Called once after the first @Input() values are set
  // Use this for initialization logic (fetch data, set defaults)
  ngOnInit() {}

  // Called once after the component’s view (and child views) are initialized
  // Use this for DOM-dependent logic. i.e. a component exists inside an an *ngIf.
  override ngAfterViewInit(): void
  {
    super.ngAfterViewInit();
  }

  // Generic utility function for any signal object
  updateSignalProperty<T, K extends keyof T>(
    signalObj: WritableSignal<T>,
    propName: K,
    newValue: T[K]
  ) {
    signalObj.update(v => ({
      ...v,
      [propName]: newValue
    }));
  }


  // Usage in template:
  // (ngModelChange)="updateProperty(formValuesSig, 'firstName', $event)"
  // Or wrap for convenience:
  // onFormValueChange<K extends keyof FormValues>(prop: K, value: FormValues[K]) {
  //   updateProperty(this.formValuesSig, prop, value);
  // }
}



